import { BlockchainType } from "@prisma/client";
import React from "react";
import { z } from "zod/v4";
import { CDNRoutes, getFullRouteUrl } from "./api";

export const isDevMode = process.env.NODE_ENV === "development";
export const isProduction = process.env.NODE_ENV === "production";
export const isTestMode = process.env.NODE_ENV === "test";

export const MAX_UNREAD_COUNT = 9;

export const TETHER_SYMBOL = "USDT";

export const denomosLogo = {
  webp: {
    light: CDNRoutes.public("denomos-logo-light.webp"),
    dark: CDNRoutes.public("denomos-logo-dark.webp"),
  },
  png: {
    light: CDNRoutes.public("denomos-logo-light.png"),
    dark: CDNRoutes.public("denomos-logo-dark.png"),
  },
} as const;

function checkTheme(theme: string): asserts theme is "light" | "dark" {
  if (!["light", "dark"].includes(theme)) {
    throw new Error(`Invalid theme: ${theme}`);
  }
}

function checkFormat(format: string): asserts format is "webp" | "png" {
  if (!["webp", "png"].includes(format)) {
    throw new Error(`Invalid format: ${format}`);
  }
}

export function getDenomosLogo(theme = "light", format = "webp") {
  checkTheme(theme);
  checkFormat(format);

  return denomosLogo[format][theme];
}

export function getDenomosIcon(size: 96 | 192 | 256 | 384 | 512 = 512) {
  return getFullRouteUrl(`/icon-${size}x${size}.png`);
}

export const ColorMap = [
  "#5E81AC", // Blue
  "#A3BE8C", // Green
  "#EBCB8B", // Yellow
  "#BF616A", // Red
  "#D08770", // Orange
  "#88C0D0", // Teal
  "#B48EAD", // Purple
  "#8FBCBB", // Turquoise
  "#F5D98B", // Gold
  "#BCAAA4", // Taupe
  "#FFB347", // Amber
  "#C5E1A5", // Chartreuse
  "#FF8A65", // Coral
  "#FFD54F", // Sunflower
  "#81D4FA", // Sky Blue
  "#CE93D8", // Orchid
  "#4DB6AC", // Mint
  "#FFAB91", // Peach
  "#90A4AE", // Steel Gray
  "#DC143C", // Crimson
  "#E6E6FA", // Lavender
  "#808000", // Olive
  "#FA8072", // Salmon
  "#6A5ACD", // Slate Blue
  "#D2691E", // Chocolate
  "#CD853F", // Peru
  "#00FA9A", // Medium Spring Green
  "#9932CC", // Dark Orchid
  "#ECEFF4", // Frost,
];

export const ColorPalette = {
  BLUE: "#5E81AC",
  GREEN: "#A3BE8C",
  YELLOW: "#EBCB8B",
  RED: "#BF616A",
  ORANGE: "#D08770",
  TEAL: "#88C0D0",
  PURPLE: "#B48EAD",
  TURQUOISE: "#8FBCBB",
  GOLD: "#F5D98B",
  TAUPE: "#BCAAA4",
  AMBER: "#FFB347",
  CHARTREUSE: "#C5E1A5",
  CORAL: "#FF8A65",
  SUNFLOWER: "#FFD54F",
  SKY: "#81D4FA",
  ORCHID: "#CE93D8",
  MINT: "#4DB6AC",
  PEACH: "#FFAB91",
  STEEL: "#90A4AE",
  CRIMSON: "#DC143C",
  LAVENDER: "#E6E6FA",
  OLIVE: "#808000",
  SALMON: "#FA8072",
  SLATE_BLUE: "#6A5ACD",
  CHOCOLATE: "#D2691E",
  PERU: "#CD853F",
  MEDIUM_SPRING_GREEN: "#00FA9A",
  DARK_ORCHID: "#9932CC",
  FROST: "#ECEFF4",
};

export const Intervals = {
  SECOND: 1000,
  MINUTE: 1000 * 60,
  HOUR: 1000 * 60 * 60,
  DAY: 1000 * 60 * 60 * 24,
  WEEK: 1000 * 60 * 60 * 24 * 7,
  MONTH: 1000 * 60 * 60 * 24 * 30,
  YEAR: 1000 * 60 * 60 * 24 * 365,
};

export const MIN_FORMATTED_BALANCE = 0.00001;

export const backgroundStyle: React.CSSProperties = {
  backgroundImage: `url(${CDNRoutes.assets}/coins-48.webp)`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  filter: "blur(1px)",
  backgroundColor: "rgba(0,0,0,1)",
};

export const DemoMode = {
  enabled: false,
  userId: "demo-user",
};

export const Denomos = {
  cash: {
    name: "Denomos Cash",
    price: 1,
    symbol: "PW-CASH",
    id: "-",
  },
} as const;

export const XHeaders = {
  IS_BOT: "x-is-bot",
  BROWSER: "x-browser",
  OS: "x-os",
  DEVICE: "x-device",
  ENGINE: "x-engine",
  CPU: "x-cpu",
};

export const DEFAULT_THEME = "dark" as const;

const browserSchema = z.object({
  name: z.string().optional(), // e.g., "Chrome", "Firefox", etc.
  version: z.string().optional(), // e.g., "92.0.4515.159"
});

const deviceSchema = z.object({
  type: z.enum(["mobile", "tablet", "desktop", "bot"]).optional(), // device type
  model: z.string().optional(), // model name, if available
  vendor: z.string().optional(), // vendor name, e.g., "Apple", "Samsung"
});

const engineSchema = z.object({
  name: z.string().optional(), // engine name, e.g., "Blink", "WebKit"
  version: z.string().optional(), // engine version
});

const osSchema = z.object({
  name: z.string().optional(), // OS name, e.g., "Windows", "iOS"
  version: z.string().optional(), // OS version
});

const userAgentSchema = z.object({
  browser: browserSchema,
  device: deviceSchema,
  engine: engineSchema,
  os: osSchema,
  isBot: z.boolean().optional(),
});

export function parseBrowser(browser: string) {
  try {
    return browserSchema.parse(JSON.parse(browser));
  } catch {
    return null;
  }
}

export function parseDevice(device: string) {
  try {
    return deviceSchema.parse(JSON.parse(device));
  } catch {
    return null;
  }
}

export function parseEngine(engine: string) {
  try {
    return engineSchema.parse(JSON.parse(engine));
  } catch {
    return null;
  }
}

export function parseOS(userAgent: string) {
  try {
    return osSchema.parse(JSON.parse(userAgent));
  } catch {
    return null;
  }
}

export function parseUserAgent(userAgent: string) {
  try {
    return userAgentSchema.parse(JSON.parse(userAgent));
  } catch {
    return null;
  }
}

export const CHAT_INBOX_SHEET_TRIGGER = "chat-inbox-trigger";

export const USER_NOTIFICATIONS_SHEET_TRIGGER = "user-notifications-trigger";

export const COIN_MARKET_DATA_START_DATE = "2020-01-01";
export const PORTFOLIO_GENESIS_DATE = "2021-01-01";

// See https://www.oklink.com/docs/en/#quickstart-guide-list-of-supported-chains
export const CHAIN_ID_TO_BLOCKCHAIN: Record<number, BlockchainType> = {
  0: "BITCOIN",
  1: "ETHEREUM",
  2: "LITECOIN",
  3: "DOGECOIN",
  5: "DASH",
  195: "TRON",
  200901: "BITLAYER",
  204: "OPBNB",
  60808: "BOB",
  11501: "BEVM",
  250: "FANTOM",
  100: "GNOSIS",
  8453: "BASE",
  534352: "SCROLL",
  169: "MANTA",
  408: "OMEGA",
  81457: "BLAST",
  33139: "APE",
  42161: "ARBITRUM",
  10: "OPTIMISM",
  59144: "LINEA",
  324: "ZKSYNC",
  137: "POLYGON",
  1101: "POLYGON_ZKEVM",
  56: "BSC",
  43114: "AVAXC",
  66: "OKTC",
  145: "BITCOIN_CASH",
  61: "ETHEREUM_CLASSIC",
  10001: "ETHW",
  513100: "DIS",
  70000014: "CANTO",
  8217: "KAIA",
  2020: "RONIN",
  70000040: "BEACON",
  9004: "STARKNET",
  196: "XLAYER",
  1625: "GRAVITY",
  223: "B2",
  5545: "DUCKCHAIN",
  637: "APTOS",
  784: "SUI",
  118: "COSMOS",
  459: "KAVA",
  501: "SOLANA",

  // Testnets
  11155111: "SEPOLIA_TESTNET",
  70000030: "GOERLI_TESTNET",
  80001: "MUMBAI_TESTNET",
  80002: "AMOY_TESTNET",
  1442: "POLYGON_ZKEVM_TESTNET",
  19500: "XLAYER_TESTNET",
} as const;

export function getBlockchainTypeFromChainId(
  chainId: number,
): BlockchainType | undefined {
  return CHAIN_ID_TO_BLOCKCHAIN[chainId];
}

export const TIMESTAMP_MS_LENGTH = 13;

export const DEFAULT_TOAST_DURATION = 5 * Intervals.SECOND;

export const X_PIXEL_SCRIPT = "https://static.ads-twitter.com/uwt.js";
export const X_PIXEL_ID = "ptdbt";
